import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock, Zap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

export default function AdminLoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      const text = typeof msg === "string" ? msg : "Login failed.";
      toast.error(text);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-5">
      <div className="relative w-full max-w-md">
        <div className="absolute -inset-4 bg-yellow-400/10 blur-3xl rounded-3xl" />
        <div className="relative bg-[#0d0d0f] border border-white/10 rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-md bg-yellow-400 flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] text-white/50 font-bold">B&J Electric</div>
              <div className="font-display font-bold text-white tracking-tight text-lg">Admin Portal</div>
            </div>
          </div>

          <h1 className="font-display font-black text-white text-3xl tracking-tighter leading-none mb-2">
            Sign in.
          </h1>
          <p className="text-white/50 text-sm mb-8">Access the B&J Electric admin dashboard.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="admin-login-form">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">Email</Label>
              <Input
                data-testid="admin-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@bjelectric.com"
                required
                className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-white/60 font-bold">Password</Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="bg-black/40 border-white/10 text-white h-11 focus-visible:ring-yellow-400/40"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 disabled:opacity-70 text-black font-bold px-6 py-3.5 rounded-md transition-all hover:scale-[1.01] active:scale-[0.99]"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {submitting ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <a href="/" className="mt-6 block text-center text-xs text-white/40 hover:text-yellow-400">
            ← Back to site
          </a>
        </div>
      </div>
    </div>
  );
}
